import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Container, Row, Column } from '@pella/sash-framework';
import { Breadcrumbs, Layout, CTA, Image, SEO } from '../../components';

import CardSection from '../../components/common/card-section';
import LocalNavTop from '../../components/common/local-nav-top';

import './index.css';

const BranchCareers = (props) => {
	const iframeRef = useRef(null);
	const pageData = props.data.contentstackBranchCareers;
	const [openPositions, setOpenPositions] = useState(null);
	const [iframeLoaded, setIframeLoaded] = useState(false);

	useEffect(() => {
		if (iframeLoaded) onIframeLoad();
	}, [iframeLoaded]);

	useEffect(() => {
		if (!openPositions) return;

		document.querySelectorAll('.careerPositions a')?.forEach((link) => {
			link.setAttribute('target', '_blank');
			link.setAttribute('rel', 'noopener noreferrer');
		});
	}, [openPositions]);

	useEffect(() => {
		if (pageData.modular_blocks?.find((block) => block.open_positions != null)?.open_positions?.job_source === 'BambooHR') {
			const jobsUrl = pageData.modular_blocks?.find((block) => block.open_positions != null)?.open_positions?.jobs_url;

			fetch(`https://${jobsUrl}/jobs/embed2.php`)
				.then((resp) => resp.text())
				.then((html) => setOpenPositions(html));
		}
	}, [pageData]);

	const onIframeLoad = () => {
		const contentHeight = iframeRef.current?.contentDocument?.body?.scrollHeight || iframeRef.current?.contentWindow?.document?.body?.scrollHeight;
		if (contentHeight > 0) {
			iframeRef.current.style.height = contentHeight + 50 + 'px';
			iframeRef.current.style.minHeight = '';
		}
	};

	return (
		<Layout id='template-BranchCareers'>
			{pageData?.branch_data?.[0]?.local_nav?.[0] && (
				<LocalNavTop
					branchData={pageData?.branch_data?.[0]}
					linksArray={pageData?.branch_data?.[0].local_nav}
					isAnchors={false}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.modular_blocks?.[0] &&
				pageData.modular_blocks.map((block, index) => {
					if (block.cards != null) {
						return (
							<CardSection
								data={block.cards}
								key={index}
								className='!py-8 xl:!py-12 [&_h2]:!text-left [&_h2]:text-xl [&_h2]:font-bold [&_h2]:md:!m-0 [&_h2]:md:pb-4 [&_h2]:md:text-22px [&_h2]:xl:text-26px [&_h2]:xl:font-semibold'
							/>
						);
					}

					if (block.columns != null && block.columns.column?.[0] && block.columns.quote) {
						const isImageLeft = block.columns.column[0].image?.[0] ? true : false;

						return (
							<div
								key={index}
								className='careersQuote pb-12 pt-6 xl:pt-12'
							>
								<Container>
									<div className={`grid items-center gap-x-2 gap-y-4 border-l-4 border-primary pl-5 md:gap-x-14 xl:gap-x-20 xl:border-l-[7px] xl:pl-10 ${isImageLeft ? 'grid-cols-[auto_1fr]' : 'grid-cols-[auto_1fr] md:grid-cols-[1fr_auto]'}`}>
										{block.columns.column.map((col, index) => (
											<React.Fragment key={index}>
												{col.image?.[0] && (
													<Image
														className={`row-span-1 h-9 w-9 rounded-full md:row-span-2 md:h-[150px] md:w-[150px] ${isImageLeft ? 'col-start-1' : 'md:col-start-2'}`}
														data={col.image[0]}
													/>
												)}

												{col.description && (
													<div
														dangerouslySetInnerHTML={{ __html: col.description }}
														className={`col-span-2 text-base md:col-span-1 md:text-xl ${isImageLeft ? 'col-start-1 md:col-start-2' : 'col-start-1'}`}
													/>
												)}

												{col.citation && (
													<div
														dangerouslySetInnerHTML={{ __html: col.citation }}
														className={`row-start-2 text-base md:text-xl [&_span]:font-bold ${isImageLeft ? 'col-start-2' : 'col-start-2 md:col-start-1'}`}
													/>
												)}
											</React.Fragment>
										))}
									</div>
								</Container>
							</div>
						);
					}

					if (block.columns != null && block.columns?.column?.[0] && !block.columns.quote) {
						const TagName = index === 0 ? 'h1' : 'h2';

						return (
							<div
								key={index}
								className='pb-6 pt-12 xl:pb-10'
								style={{ backgroundColor: block.columns.background_hex_value }}
							>
								<Container>
									{block.columns.heading && <TagName className='mb-12 text-center font-normal !leading-tight md:text-28px md:font-semibold'>{block.columns.heading}</TagName>}

									<Row
										className='max-md:gap-y-4'
										align={block.columns.align}
									>
										{block.columns.column.map((col, index) => {
											return (
												<Column
													key={index}
													xs={12}
													md={col.column_desktop}
												>
													<div
														className='h-full'
														style={{ backgroundColor: col.container_hex_value }}
													>
														{col.image?.[0] && <Image data={col.image[0]} />}

														<div className={col.container_hex_value ? 'px-5 py-8 md:px-6 md:py-12 xl:px-12' : ''}>
															{col.heading && (
																<h2
																	className='m-0 text-xl font-bold !leading-tight max-md:tracking-[0.15px] md:text-22px xl:text-26px xl:font-semibold'
																	dangerouslySetInnerHTML={{ __html: col.heading }}
																/>
															)}

															{col.description && (
																<div
																	className='text-sm font-normal md:text-base [&_li]:pl-2.5 [&_li]:pt-3 [&_p]:py-4'
																	dangerouslySetInnerHTML={{ __html: col.description }}
																/>
															)}

															{col.cta?.[0] && (
																<CTA
																	data={col.cta[0]}
																	className='mt-4'
																/>
															)}
														</div>
													</div>
												</Column>
											);
										})}
									</Row>
								</Container>
							</div>
						);
					}

					if (block.feature != null) {
						const TagName = index === 0 ? 'h1' : 'h2';
						return (
							<div
								key={index}
								className='py-6 md:py-8 xl:py-10'
							>
								<Container>
									<Row
										className='max-md:gap-y-4'
										align='center'
										justify='center'
									>
										{block.feature.image?.[0] && (
											<Column
												xs={12}
												xl='auto'
											>
												<Image
													className='!block aspect-[3/2] xl:aspect-[4/3]'
													data={block.feature.image[0]}
												/>
											</Column>
										)}
										<Column
											xs={12}
											xl='fluid'
										>
											{block.feature.heading && (
												<TagName
													className='m-0 pt-6 text-32px font-semibold !leading-tight max-md:tracking-[0.15px] md:pt-9 md:text-42px xl:pt-0'
													dangerouslySetInnerHTML={{ __html: block.feature.heading }}
												/>
											)}

											{block.feature.description && (
												<div
													className='py-5 text-sm font-normal md:py-6 md:text-base'
													dangerouslySetInnerHTML={{ __html: block.feature.description }}
												/>
											)}

											{block.feature.cta?.[0] && <CTA data={block.feature.cta[0]} />}
										</Column>
									</Row>
								</Container>
							</div>
						);
					}

					if (block.open_positions != null) {
						const jazzhrSrc = `<div><script type="text/javascript" src="${block.open_positions.jobs_url}" charset="utf-8"></script></div>`;
						return (
							<div
								key={index}
								id={block.open_positions.anchor_text}
								className='py-10 md:py-12 xl:py-16'
							>
								<Container>
									{!openPositions && (
										<div className={block.open_positions?.cta?.[0] ? 'text-center' : ''}>
											{block.open_positions.heading && <h2 className='mb-4 md:mb-6 xl:mb-8'>{block.open_positions.heading}</h2>}
											{block.open_positions.cta?.[0] && <CTA data={block.open_positions.cta[0]} />}
										</div>
									)}

									{block.open_positions.job_source === 'BambooHR' && openPositions && (
										<>
											<link
												href={`https://${block.open_positions.jobs_url}/css/jobs-embed.css`}
												rel='stylesheet'
											/>
											<div
												className='careerPositions [&_h2]:!pb-8 [&_h2]:!font-sans [&_h2]:!text-26px [&_h2]:!text-black md:[&_h2]:!text-32px xl:[&_h2]:!pb-14 xl:[&_h2]:!text-4xl xl:[&_h2]:!font-semibold'
												dangerouslySetInnerHTML={{ __html: openPositions }}
											/>
										</>
									)}

									{(block.open_positions.job_source === 'JazzHR' || !block.open_positions.job_source) && (
										<iframe
											title='Open Positions'
											className='w-full'
											srcDoc={block.open_positions.job_source === 'JazzHR' ? jazzhrSrc : null}
											src={!block.open_positions.job_source ? block.open_positions.jobs_url : null}
											ref={iframeRef}
											style={{ height: '60vh' }}
											onLoad={!block.open_positions.job_source ? null : () => setIframeLoaded(true)}
										/>
									)}
								</Container>
							</div>
						);
					}
				})}
		</Layout>
	);
};

BranchCareers.propTypes = {
	data: PropTypes.object,
	branch: PropTypes.object,
	className: PropTypes.string,
};

export default BranchCareers;

// eslint-disable-next-line react/prop-types -- gatsby component
export const Head = ({ data }) => <SEO {...data.contentstackBranchCareers.seo} />;

export const query = graphql`
	query ($uid: String!) {
		contentstackBranchCareers(id: { eq: $uid }) {
			url
			branch_data {
				...BranchData
			}
			breadcrumbs {
				...Breadcrumbs
			}
			modular_blocks {
				cards {
					...CardSection
				}
				columns {
					background_hex_value
					heading
					quote
					align
					column {
						citation
						column_desktop
						container_hex_value
						cta {
							...CTA
						}
						description
						heading
						image {
							...Image
							image {
								url
								gatsbyImageData(quality: 90)
							}
						}
					}
				}
				open_positions {
					anchor_text
					heading
					job_source
					jobs_url
					cta {
						...CTA
					}
				}
				feature {
					description
					heading
					cta {
						...CTA
					}
					image {
						...Image
						image {
							url
							gatsbyImageData(quality: 90)
						}
					}
				}
			}
			seo {
				...SEO
			}
		}
	}
`;
